import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { INK, NEUTRAL, SHIFT_NIGHT_FALLBACK, SURFACE } from "../../theme/tokens";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import { deepCloneObject } from "../../utils/dateUtils";
import { type NurseInShift, type NurseInShiftDetails, NurseTrainingStatus, ShiftType } from "../../utils/models";
import { inverseRolesMap } from "../../utils/utilFunctions";
import { EditTimePopover } from "../EditTimePopover";
import { MobileNurseItem } from "../staffing/mobile/NursesList/MobileNurseItem";
import { CommonHeader } from "../UIToolkit/molecules/CommonHeader";
import { JobSelector } from "../UIToolkit/molecules/JobSelector";
import { ShiftDataNurseRemoval } from "./ShiftDataNurseRemoval";
import { ShiftDataRoomList } from "./ShiftDataRoomList";
export const ShiftData: FC = () => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const returnTo = searchParams.get("returnTo");
	const { getNurseInShiftDetailsById, showErrorSnackbar, updateNurseTimes, hospitalData, chosenDepartment, refreshDepartmentData } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { updateNurse, isLoading } = useHandleNurseChange();
	const [isEditingTime, setIsEditingTime] = useState<boolean>(false);
	const [anchorRef, setAnchorRef] = useState<HTMLElement>();

	const nurseInShiftDetails: NurseInShiftDetails | undefined = getNurseInShiftDetailsById(id ?? "");
	const shiftNo: number = nurseInShiftDetails?.nurse?.shiftType === ShiftType.Day ? 0 : 1;

	const [selectedRoom, setSelectedRoom] = useState<string>(nurseInShiftDetails?.nurse.location ?? "");
	const [shiftStart, setShiftStart] = useState<DateTime>(nurseInShiftDetails?.nurse.startTime ?? DateTime.now());
	const [shiftEnd, setShiftEnd] = useState<DateTime>(nurseInShiftDetails?.nurse.endTime ?? DateTime.now());
	const [isCharge, setIsCharge] = useState<boolean>(nurseInShiftDetails?.nurse.charge ?? false);
	const [absenceMessage, setAbsenceMessage] = useState<string>("");
	const [isTrainee, setIsTrainee] = useState<boolean>(nurseInShiftDetails?.nurse.trainingStatus === NurseTrainingStatus.Trainee);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>(nurseInShiftDetails?.nurse.jobId);

	const nurse = hospitalData.nurses.find((n) => n.id === nurseInShiftDetails?.nurse.nurseId);
	const hasMultipleJobs = (nurse?.jobs?.length ?? 0) > 1;

	const isRemoveButtonEnabled = !!absenceMessage && !(isLoading.removeNurse || isLoading.updateNurse);

	const getReturnPath = (): string => returnTo ?? `/${chosenDepartment.department_id}/staffing/${shiftStart.toFormat("yyyy-MM-dd")}`;

	const saveChanges = async (): Promise<void> => {
		try {
			if (isLoading.updateNurse) return;

			trackEvent("save_changes_mobile", { nurseInShiftDetails });

			const updatedNurse: NurseInShift = deepCloneObject(nurseInShiftDetails?.nurse)!;
			updatedNurse.startTime = shiftStart;
			updatedNurse.endTime = shiftEnd;
			updatedNurse.trainingStatus = isTrainee ? NurseTrainingStatus.Trainee : NurseTrainingStatus.Regular;
			updatedNurse.charge = isCharge;
			updatedNurse.location = selectedRoom;
			updatedNurse.jobId = selectedJobId;
			updatedNurse.jobName = nurse?.jobs?.find((j) => j.id === selectedJobId)?.displayName ?? "";
			if (!nurseInShiftDetails) {
				trackErrorEvent("cannot_save_shift_data_no_nurse_in_shift_details", {
					id,
					nurseInShiftDetails,
				});
				return;
			}
			await updateNurse({
				nurse: updatedNurse,
				shiftIndex: shiftNo,
				dayNo: nurseInShiftDetails.dayIndex,
				weekIndex: nurseInShiftDetails.weekIndex,
			});
			updateNurseTimes(updatedNurse.id, nurseInShiftDetails.weekIndex, shiftNo, nurseInShiftDetails.dayIndex, shiftStart, shiftEnd, false);
			await refreshDepartmentData(chosenDepartment.department_id, false);
			navigate(getReturnPath());
			showSnackbar(
				t("nurse.updated.success", {
					nurseName: nurseInShiftDetails?.nurse?.name,
				}),
				"success",
			);
		} catch (e) {
			showErrorSnackbar("Failed to save changes", { nurseId: nurseInShiftDetails?.nurse.id }, e);
			return;
		}
	};

	return (
		<Box
			sx={{
				bgcolor: SURFACE,
				maxWidth: "560px",
				margin: "0 auto",
				borderRadius: "8px",
				boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
				display: "flex",
				flexDirection: "column",
				p: 1,
				height: "100%",
				overflowY: "auto",
			}}
		>
			<CommonHeader title={"Edit Shift"} onClose={() => navigate(getReturnPath())} />

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					overflowY: "auto",
					height: "calc(100% - 40px)",
				}}
			>
				<Box sx={{ px: 2, py: 1.5, height: "fit-content" }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							mb: 2,
						}}
					>
						<Box
							sx={{
								width: "100%",
							}}
						>
							{nurseInShiftDetails && (
								<MobileNurseItem
									nurse={nurseInShiftDetails.nurse}
									weekIndex={nurseInShiftDetails.weekIndex}
									dayIndex={nurseInShiftDetails.dayIndex}
									background
								/>
							)}
						</Box>
					</Box>

					{!!nurseInShiftDetails && (
						<ShiftDataRoomList
							nurseId={nurseInShiftDetails.nurse.nurseId}
							weekIndex={nurseInShiftDetails.weekIndex}
							dayIndex={nurseInShiftDetails.dayIndex}
							role={nurseInShiftDetails.nurse.role}
							shiftIndex={shiftNo}
							room={selectedRoom}
							setRoom={setSelectedRoom}
						/>
					)}

					{hasMultipleJobs && (
						<Box sx={{ mb: 2 }}>
							<Typography sx={{ color: "primary.main", mb: 0.5, fontSize: "13px" }}>Job</Typography>
							<JobSelector
								jobs={nurse?.jobs ?? []}
								selectedJobIds={selectedJobId ? [selectedJobId] : []}
								onSelectedJobsChanged={(ids) => setSelectedJobId(ids[0])}
								singleSelect
								hideAllOption
								variant="dropdown"
							/>
						</Box>
					)}

					<Box sx={{ mb: 2 }}>
						<Typography
							sx={{
								color: "primary.main",
								mb: 0.5,
								fontSize: "13px",
							}}
						>
							Choose Hours
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								border: `1px solid ${NEUTRAL[300]}`,
								borderRadius: "4px",
								p: 1,
								height: "40px",
								justifyContent: "space-between",
							}}
							onClick={(e) => {
								setAnchorRef(e.currentTarget);
								setIsEditingTime(true);
							}}
						>
							<Typography>
								{shiftStart.toFormat("h:mm a")} - {shiftEnd.toFormat("h:mm a")}
							</Typography>
							<AccessTimeIcon sx={{ color: "primary.main" }} />
						</Box>
						{isEditingTime && (
							<EditTimePopover
								role={nurse ? inverseRolesMap[nurse.role] : undefined}
								jobId={selectedJobId}
								startTime={shiftStart}
								endTime={shiftEnd}
								onTimeChange={({ newStartTime, newEndTime }) => {
									trackEvent("change_shift_times_mobile", {
										shiftStart,
										shiftEnd,
										shiftNo,
										nurse: nurseInShiftDetails?.nurse,
									});
									setShiftStart(newStartTime);
									setShiftEnd(newEndTime);
								}}
								onClose={() => setIsEditingTime(false)}
								anchorEl={anchorRef!}
								isActual={false}
							/>
						)}
					</Box>

					<Box sx={{ gap: 2, flexDirection: "column", display: "flex" }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isCharge}
									onChange={({ target }) => setIsCharge(target.checked)}
									sx={{
										color: "primary.main",
										padding: "4px",
										"&.Mui-checked": {
											color: SHIFT_NIGHT_FALLBACK,
										},
									}}
								/>
							}
							label={<Typography sx={{ color: INK.SECONDARY, fontSize: "14px" }}>Make Charge</Typography>}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={isTrainee}
									onChange={({ target }) => setIsTrainee(target.checked)}
									sx={{
										color: "primary.main",
										padding: "4px",
										"&.Mui-checked": {
											color: SHIFT_NIGHT_FALLBACK,
										},
									}}
								/>
							}
							label={<Typography sx={{ color: INK.SECONDARY, fontSize: "14px" }}>Make Trainee</Typography>}
						/>
					</Box>

					{nurseInShiftDetails && (
						<ShiftDataNurseRemoval
							nurse={nurseInShiftDetails.nurse}
							weekIndex={nurseInShiftDetails.weekIndex}
							dayIndex={nurseInShiftDetails.dayIndex}
							shiftNo={shiftNo}
							absenceMessage={absenceMessage}
							setAbsenceMessage={setAbsenceMessage}
							date={nurseInShiftDetails.nurse.startTime}
							isRemoveButtonEnabled={isRemoveButtonEnabled}
						/>
					)}
					{!isRemoveButtonEnabled && (
						<Button
							fullWidth
							variant="contained"
							onClick={saveChanges}
							disabled={isLoading.removeNurse || isLoading.updateNurse}
							sx={{
								bgcolor: "primary.main",
								color: "white",
								textTransform: "none",
								"&:hover": {
									bgcolor: "primary.dark",
								},
								height: "40px",
								fontSize: "14px",
							}}
						>
							Save Changes
						</Button>
					)}
				</Box>
			</Box>
		</Box>
	);
};
